/**
 * THE DESK'S SELECTION ACTS, IN THE HEADER (0.1.528; maintainer, 3 Oct 2026: "On desktop, when in
 * tabs selecting row, the actions for those selected items should pop up in the header to the left
 * of the search box").
 *
 * Ruling: on the desk (≥701px) a tab's acts are pills in its header band, and the floating
 * `Actions (N)` pill goes. The phone keeps its float and its acts sheet: there is no band width to
 * hold them, and rule 5's sheet is still the phone's answer.
 *
 * ⚠️ THE ACTS REPLACE THE WHOLE BAND NOW (`replace`; maintainer, 3 Oct 2026: "on desk tabs selections
 * Actions appear in the header should replace the whole header, just like happens in the items tab.
 * And all the actions should be displayed. Just like the item tab, first show N amount selected,
 * clear selection button, then the delete and retire button, then a small vertical break line, then
 * the rest of the actions. If there is overflow, then allow for scrolling sideways"). So, with
 * `replace`: `✓ N selected` · Clear selection · the destructive acts IN THE ORDER THE CALLER LISTS
 * THEM (Delete before Retire, the maintainer's words' order) · a 1px divider · every other act. One
 * line, never wrapped; it scrolls sideways inside itself. Every tab band (Boxes, Outfits,
 * Reservations, Check-outs, Projects, Racks) AND the Items tab's bulk bar ride it. How the bar takes
 * the band's place without moving a row is CSS (styles.css, "THE BAR REPLACES THE BAND").
 *
 * Hoisted, not copied: each screen passes its EXISTING handlers — this component holds no act logic,
 * only the order and the look, so the order cannot drift per tab.
 *
 * Without `replace` (one caller left: the reservation's Items list, `.items-slot`, whose own ruled
 * order is below at `destructiveFirst`) the acts sit inline where the caller puts them.
 */
export interface HeaderAct {
  label: string;
  onClick: () => void;
  disabled?: boolean | undefined;
  /**
   * `danger` = irreversible (a delete, a retire): `btn-danger`'s filled alert pill. With `replace` the
   * danger acts lead (right after Clear); without it they end the row (DESIGN.md rule 5) unless
   * `destructiveFirst`.
   * THE ADD PAIR (0.1.531; maintainer, 3 Oct 2026: "the buttons that add to an existing something to
   * be filled green buttons, and the buttons that add to something new should be a green outline and
   * green text without fill" — the reservation's add doors, DESIGN.md "Add doors"): `existing` is
   * `btn-primary`'s solid green, `new` is `btn-outline-accent`. The caller lists existing BEFORE new.
   * Anything else is a plain `chip`.
   */
  tone?: 'plain' | 'danger' | 'existing' | 'new' | undefined;
}

export function HeaderActs({
  count,
  onClear,
  acts = [],
  extra,
  label,
  showCount = true,
  announce = false,
  escHint = false,
  destructiveFirst = false,
  replace = false,
  lead,
  tail,
  countClassName,
  className,
}: {
  count: number;
  onClear: () => void;
  acts?: readonly HeaderAct[];
  /**
   * Caller-built pills that are not a plain {label, onClick} — e.g. a reservation's
   * `extraBulkActions` ("Remove from this reservation", already `chip-danger`). Drawn after the
   * plain acts and BEFORE the `danger` ones, so a structured destructive act still ends the row
   * (with `destructiveFirst`, the same pair moves to just after Clear).
   */
  extra?: React.ReactNode;
  /** The toolbar's accessible name — "Actions for the selected boxes". */
  label: string;
  /** False where the surrounding row already reads "N selected". Ignored with `replace`: the bar
   *  hides the band, meta and all, so the bar must say the number itself. */
  showCount?: boolean;
  /** `role="status"` on the count — only where a list already announced it (the reservation's
   *  items row did before 0.1.528); several live regions on one tab would talk over each other. */
  announce?: boolean;
  /** "Clear selection · Esc" — only on a list whose screen really clears on Esc (Items). A key hint
   *  for a key that does nothing would be folklore in reverse. */
  escHint?: boolean;
  /**
   * DESTRUCTIVE RIGHT AFTER CLEAR, not last — `extra` and the `danger` acts are drawn between
   * `Clear selection` and the plain acts. ONE caller: the reservation's Items list (0.1.528;
   * maintainer, 3 Oct 2026: "put the remove button right of the clear button"), whose one
   * destructive act — "Remove from this reservation" — is the row's main job, the two adds being
   * errands to elsewhere. Opt-in, so every other tab keeps rule 5's destructive-last; default false.
   */
  destructiveFirst?: boolean;
  /**
   * THE HEADER-REPLACING FORM (maintainer, 3 Oct 2026 — see the file head). Implies destructive-first and draws the
   * divider between the destructive group and the rest (only when both groups have something — a
   * line with nothing on one side of it divides nothing). The count always shows: the band's own
   * "N selected" meta is hidden under the bar. Desk only — callers render it only off the phone.
   */
  replace?: boolean;
  /** Drawn right after the count, before Clear — the Items bar's phone-width "Actions (n)" door
   *  (frame 1b), which CSS shows only at that width. */
  lead?: React.ReactNode;
  /** Caller-built ORDINARY acts, drawn after the plain acts — the box detail's Keep item / Item
   *  returned / Remove from box (`extraBulkActions`), which sat behind Items' More ▾ until the 3 Oct 2026
   *  "all the actions should be displayed" ruling. */
  tail?: React.ReactNode;
  /** An extra class on the count — Items keeps `.items-bulkbar-count`, which select.spec reads. */
  countClassName?: string | undefined;
  className?: string | undefined;
}): JSX.Element {
  const plain = acts.filter((a) => a.tone !== 'danger');
  const danger = acts.filter((a) => a.tone === 'danger');
  const plainEls = plain.map((a) => (
    <button
      key={a.label}
      type="button"
      className={a.tone === 'existing' ? 'btn-primary chip' : a.tone === 'new' ? 'btn-outline-accent chip' : 'chip'}
      disabled={a.disabled}
      onClick={a.onClick}
    >
      {a.label}
    </button>
  ));
  const destructiveEls = (
    <>
      {extra}
      {danger.map((a) => (
        <button key={a.label} type="button" className="btn-danger chip" disabled={a.disabled} onClick={a.onClick}>
          {a.label}
        </button>
      ))}
    </>
  );
  const hasDestructive = (extra !== undefined && extra !== null && extra !== false) || danger.length > 0;
  const hasOthers = plain.length > 0 || (tail !== undefined && tail !== null && tail !== false);
  const classes = ['header-acts'];
  if (replace) classes.push('header-acts-replace');
  if (className !== undefined) classes.push(className);
  return (
    <div className={classes.join(' ')} role="toolbar" aria-label={label}>
      {(showCount || replace) && (
        <strong
          className={countClassName === undefined ? 'header-acts-count' : `header-acts-count ${countClassName}`}
          {...(announce ? { role: 'status' } : {})}
        >
          ✓ {count} selected
        </strong>
      )}
      {lead}
      <button type="button" className="chip chip-clear" onClick={onClear}>
        {escHint ? 'Clear selection · Esc' : 'Clear selection'}
      </button>
      {replace || destructiveFirst ? (
        <>
          {destructiveEls}
          {/* THE SMALL VERTICAL BREAK LINE (maintainer, 3 Oct 2026) — decorative, so hidden from
              assistive tech; the toolbar's buttons are the content. */}
          {replace && hasDestructive && hasOthers && <span className="header-acts-divider" aria-hidden="true" />}
          {plainEls}
          {tail}
        </>
      ) : (
        <>
          {plainEls}
          {tail}
          {destructiveEls}
        </>
      )}
    </div>
  );
}
